import { MenuIcon, PenIcon, TrashIcon } from "@xola/icons";
import { flash, Label, Table } from "@xola/ui-kit";
import clsx from "clsx";
import { DemographicIcon } from "common";
import { produce } from "immer";
import { findIndex, isNil, omit, sortBy } from "lodash";
import React from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import {
    CatalogItem,
    CatalogItemSequences,
    CatalogTemplateItem,
    Experience,
    LineItemTemplate,
    LineItemType,
    ProductCatalog,
    ProductCatalogItem,
    UnitType,
} from "types";
import { AddCatalogItemPopover } from "./AddCatalogItemPopover";
import { EditCatalogItemModal } from "./EditCatalogItemModal";
import { InlineConfirmDeletePopover } from "@/components/Common/InlineConfirmDeletePopover";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useDraggableCatalogItems } from "@/hooks/useDraggableCatalogItems";
import { populateMissingSequences, roundDownSequence } from "@/models/CatalogItem";
import { isDefaultDemographic } from "@/models/DemographicUnit";

const isNewSecondaryDemographicCatalogItem = (item: CatalogItem) =>
    item.unitType === UnitType.DEMOGRAPHIC_SECONDARY && !item.id;

interface SecondaryDemographicCatalogEditorProps {
    value: ProductCatalog;
    experience: Experience;
    error?: string;
    onChange: (catalog: ProductCatalog) => void;
}

export const SecondaryDemographicCatalogEditor = ({
    value,
    experience,
    error,
    onChange,
}: SecondaryDemographicCatalogEditorProps) => {
    const { data: secondaryTemplates } = useAutoFetchCollection<LineItemTemplate>("lineItemTemplates", {
        type: LineItemType.DEMOGRAPHIC_SECONDARY,
    });

    const catalogItems = value.items.filter((catalogItem) => {
        return catalogItem.unitType === UnitType.DEMOGRAPHIC_SECONDARY;
    });
    const isSequenceMissing = catalogItems.find((catalogItem) => !catalogItem.sequence);
    const demographicCatalogItems = (
        isSequenceMissing ? populateMissingSequences(catalogItems) : catalogItems
    ) as ProductCatalogItem[];

    const handleInsertCatalogItem = (catalogItem: ProductCatalogItem) => {
        const unitId = catalogItem.unit?.id;
        const existingCatalogItem = demographicCatalogItems.find((item) => {
            return item.unit?.id === unitId;
        });
        if (existingCatalogItem) {
            flash.danger("This demographic already exists");
            return;
        }

        const deletedCatalogItem = experience.catalog.items.find((item) => {
            return item.unitType === UnitType.DEMOGRAPHIC_SECONDARY && item.unit?.id === unitId;
        });

        const secondaryTemplate = secondaryTemplates?.find((template) => template.unit?.id === unitId);

        const catalog = produce(value, (draft) => {
            draft.items.push(
                deletedCatalogItem ?? {
                    ...omit(catalogItem, ["id"]),
                    unitType: UnitType.DEMOGRAPHIC_SECONDARY,
                    parent: { id: catalogItem.id! },
                    overrideParent: true,
                    template: (secondaryTemplate as CatalogTemplateItem) ?? catalogItem.template,
                },
            );
        });

        const newItems = catalog.items.filter((item) => isNewSecondaryDemographicCatalogItem(item));

        if (newItems.length > 1) {
            const newCatalog = produce(catalog, (draft) => {
                const items: CatalogItem[] = [];
                const demographicItemsWithSequence = sortBy(
                    catalog.items.filter(
                        (item) => item.unitType === UnitType.DEMOGRAPHIC_SECONDARY && !isNil(item.sequence),
                    ),
                    "sequence",
                );
                let lastUsedSequence =
                    demographicItemsWithSequence?.[demographicItemsWithSequence.length - 1]?.sequence ?? 0;

                for (const item of sortBy(draft.items, "sequence")) {
                    if (isNewSecondaryDemographicCatalogItem(item) && isNil(item.sequence)) {
                        const newSequence = roundDownSequence(lastUsedSequence) + CatalogItemSequences.STEP;
                        const updatedItem = {
                            ...item,
                            sequence: newSequence,
                        };

                        lastUsedSequence = updatedItem.sequence;
                        items.push(updatedItem);
                    } else {
                        items.push(item);
                    }
                }

                draft.items = items;
            });

            return onChange(newCatalog);
        }

        onChange(catalog);
    };

    const handleDeleteCatalogItem = (catalogItem: ProductCatalogItem) => {
        const catalog = produce(value, (draft) => {
            draft.items = draft.items.filter((item) => {
                return item.template?.id !== catalogItem.template?.id;
            });
        });
        onChange(catalog);
    };

    const handleUpdateCatalogItem = (catalogItem: ProductCatalogItem) => {
        const catalog = produce(value, (draft) => {
            const index = findIndex(draft.items, ["template.id", catalogItem.template?.id]);
            if (index >= 0) {
                draft.items[index] = catalogItem;
            }
        });

        onChange(catalog);
    };

    return (
        <div className="space-y-2">
            <div className="mb-2 flex items-center justify-between">
                <Label className={error && "!text-danger"}>Demographics {error ? <span>{error}</span> : null}</Label>
                <AddCatalogItemPopover onInsert={handleInsertCatalogItem} />
            </div>
            <SecondaryDemographicCatalogItemList
                value={value}
                catalogItems={demographicCatalogItems}
                onChange={onChange}
                onDeleteCatalogItem={handleDeleteCatalogItem}
                onUpdateCatalogItem={handleUpdateCatalogItem}
            />
        </div>
    );
};

interface SecondaryDemographicCatalogItemListProps {
    value: ProductCatalog;
    catalogItems: ProductCatalogItem[];
    onChange: (catalog: ProductCatalog) => void;
    onDeleteCatalogItem: (catalogItem: ProductCatalogItem) => void;
    onUpdateCatalogItem: (catalogItem: ProductCatalogItem) => void;
}

export const SecondaryDemographicCatalogItemList = ({
    value,
    catalogItems,
    onChange,
    onDeleteCatalogItem,
    onUpdateCatalogItem,
}: SecondaryDemographicCatalogItemListProps) => {
    const {
        items: demographicItems,
        showEditCatalogItemModal,
        selectedCatalogItem,
        toggleEditCatalogItemModal,
        handleSubmit,
        handleDragEnd,
        handleItemDelete,
    } = useDraggableCatalogItems({
        value,
        catalogItems,
        unitType: UnitType.DEMOGRAPHIC_SECONDARY,
        onChange,
        onDeleteCatalogItem,
        onUpdateCatalogItem,
    });

    return (
        <>
            <DragDropContext onDragEnd={(result: DropResult) => handleDragEnd(result)}>
                <Droppable
                    droppableId="secondaryDemographicTable"
                    renderClone={(provided, _, rubric) => (
                        <SecondaryDemographicDragCloneRow
                            provided={provided}
                            item={demographicItems[rubric.source.index]}
                            index={rubric.source.index}
                            toggleEditCatalogItemModal={toggleEditCatalogItemModal}
                            handleItemDelete={handleItemDelete}
                        />
                    )}
                >
                    {(provided: DroppableProvided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header className="!text-sm" />
                                        <Table.Header className="!text-sm">Demographic</Table.Header>
                                        <Table.Header className="!text-sm">Caption</Table.Header>
                                        <Table.Header className="text-right !text-sm">Actions</Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped={false} className="max-h-51 overflow-y-scroll">
                                    {demographicItems.length > 0 ? (
                                        <>
                                            {demographicItems.map((item, index: number) => (
                                                <Draggable
                                                    key={item.template?.id ?? item.name}
                                                    draggableId={item.template?.id ?? item.name}
                                                    index={index}
                                                >
                                                    {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
                                                        <tr
                                                            ref={innerRef}
                                                            {...draggableProps}
                                                            {...dragHandleProps}
                                                            key={item.template!.id}
                                                            className={clsx("justify-between", {
                                                                shadow: snapshot.isDragging,
                                                            })}
                                                            data-testid="secondary-demographic-catalog-editor-row"
                                                        >
                                                            <Table.Cell className="w-1/12">
                                                                <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                                                                    <MenuIcon size="small" />
                                                                </span>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-4/12">
                                                                <NoTranslate
                                                                    if={!isDefaultDemographic(item.name)}
                                                                    className="whitespace-normal break-all"
                                                                >
                                                                    <DemographicIcon name={item.name} /> {item.name}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-5/12">
                                                                <NoTranslate className="whitespace-normal break-all">
                                                                    {item.caption}
                                                                </NoTranslate>
                                                            </Table.Cell>
                                                            <Table.Cell className="w-2/12 space-x-1 text-right">
                                                                <SecondaryButton
                                                                    className="mb-1"
                                                                    data-testid="secondary-demographic-catalog-editor-edit"
                                                                    size="tiny"
                                                                    onClick={(event: any) =>
                                                                        toggleEditCatalogItemModal(event, item)
                                                                    }
                                                                >
                                                                    <PenIcon />
                                                                </SecondaryButton>
                                                                <InlineConfirmDeletePopover
                                                                    onConfirm={() => handleItemDelete(index, item)}
                                                                >
                                                                    <SecondaryButton
                                                                        data-testid="secondary-demographic-catalog-editor-remove"
                                                                        className="mb-1"
                                                                        size="tiny"
                                                                    >
                                                                        <TrashIcon />
                                                                    </SecondaryButton>
                                                                </InlineConfirmDeletePopover>
                                                            </Table.Cell>
                                                        </tr>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </>
                                    ) : (
                                        <Table.Row>
                                            <Table.Cell colSpan={4} className="text-center">
                                                No Demographics
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <EditCatalogItemModal
                isOpen={showEditCatalogItemModal}
                catalogItem={selectedCatalogItem!}
                onClose={toggleEditCatalogItemModal}
                onSubmit={handleSubmit}
            />
        </>
    );
};

const SecondaryDemographicDragCloneRow = ({
    provided,
    item,
    index,
    toggleEditCatalogItemModal,
    handleItemDelete,
}: {
    provided: any;
    item: any;
    index: number;
    toggleEditCatalogItemModal: (event: any, item: any) => void;
    handleItemDelete: (index: number, item: any) => void;
}) => {
    return (
        <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={provided.draggableProps.style}
            className="pointer-events-none bg-white"
        >
            <Table.Cell className="w-1/12">
                <span className="inline-flex rounded border border-gray-light px-2 py-0.5">
                    <MenuIcon size="small" />
                </span>
            </Table.Cell>
            <Table.Cell className="w-4/12">
                <NoTranslate if={!isDefaultDemographic(item.name)} className="whitespace-normal break-all">
                    <DemographicIcon name={item.name} /> {item.name}
                </NoTranslate>
            </Table.Cell>
            <Table.Cell className="w-5/12">
                <NoTranslate className="whitespace-normal break-all">{item.caption}</NoTranslate>
            </Table.Cell>
            <Table.Cell className="w-2/12 space-x-1 text-right">
                <SecondaryButton
                    className="mb-1"
                    data-testid="secondary-demographic-catalog-editor-edit"
                    size="tiny"
                    onClick={(event: any) => toggleEditCatalogItemModal(event, item)}
                >
                    <PenIcon />
                </SecondaryButton>
                <InlineConfirmDeletePopover onConfirm={() => handleItemDelete(index, item)}>
                    <SecondaryButton
                        data-testid="secondary-demographic-catalog-editor-remove"
                        className="mb-1"
                        size="tiny"
                    >
                        <TrashIcon />
                    </SecondaryButton>
                </InlineConfirmDeletePopover>
            </Table.Cell>
        </tr>
    );
};
